import { ArrowDownLeft, ArrowUpRight, Ban, Copy, Percent, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import MerchantIcon, { matchBrand } from "./MerchantIcon";
import { categoryIconFor } from "./CategoryIcon";
import { mccCategory } from "./mccCategories";

// Карточка одной операции, поднимающаяся над затемнённой сценой.
//
// Показываем ТОЛЬКО то, что действительно пришло от эмитента. Комиссия по
// покупке приходит отдельной строкой ленты, а не полем внутри операции —
// пририсовать её сюда значило бы придумать число.

const money = (minor, scale = 2) =>
  minor === null || minor === undefined ? "—" : `$${(minor / 10 ** scale).toFixed(2)}`;

const STATUS = {
  completed: ["op_status_done", "ok"],
  settled: ["op_status_done", "ok"],
  pending: ["op_hold", "wait"],
  processing: ["op_processing", "wait"],
  declined: ["op_decline", "bad"],
  refunded: ["op_failed", "bad"],
  failed: ["op_failed", "bad"],
};

const OwnIcon = ({ icon: Icon, tone, incoming }) => (
  <Icon
    className={cn(
      "h-5 w-5",
      tone === "bad" ? "text-destructive-fg" : incoming ? "text-emerald-500" : "text-muted-foreground"
    )}
  />
);

const OperationSheet = ({ op, last4, onClose }) => {
  const { t } = useTranslation();
  if (!op) return null;

  const incoming = op.direction === "in";
  // Логотип уместен только у настоящей покупки. У нашего пополнения, вывода,
  // комиссии и отказа важен ТИП операции — монограмма «ПО» вместо значка
  // выглядела бы как сбой, а не как бренд.
  const ownIcon =
    op.source === "ours"
      ? op.type === "fund"
        ? ArrowDownLeft
        : ArrowUpRight
      : op.type === "fee"
      ? Percent
      : op.status === "declined"
      ? Ban
      : null;
  const [statusKey, tone] = STATUS[op.status] || ["op_status_done", "ok"];
  const branded = !ownIcon && Boolean(op.merchantName) && Boolean(matchBrand(op.merchantName));
  // Та же очередь, что и в ленте: бренд, затем категория, затем монограмма.
  const category = mccCategory(op.mcc);
  const CategoryGlyph = !ownIcon && !branded ? categoryIconFor(op.mcc) : null;

  const title =
    op.source === "ours"
      ? op.type === "fund"
        ? t("cards.op_fund")
        : t("cards.op_unload")
      : op.merchantName || (op.type === "fee" ? t("cards.op_fee") : t("cards.op_purchase"));

  const when = op.occurredAt ? String(op.occurredAt).slice(0, 16).replace("T", " ") : null;

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(t("cards.copied", { what: label }));
    } catch (_) {
      toast.error(t("cards.copy_error"));
    }
  };

  const rows = [
    last4 ? [t("cards.op_card"), `•••• ${last4}`, null] : null,
    op.mccDescription || op.mcc
      ? [
          t("cards.op_category"),
          op.mccDescription
            || (category ? `${t(`cards.mcc_${category}`)} · ${op.mcc}` : `MCC ${op.mcc}`),
          null,
        ]
      : null,
    op.declineCode ? [t("cards.op_decline"), op.declineCode, null] : null,
    op.id ? [t("cards.op_id"), String(op.id), String(op.id)] : null,
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-[96] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <span className="lp-scrim" onClick={onClose} aria-hidden="true" />

      <div className="lp-sheet relative w-full max-w-md px-4 pb-[calc(1.25rem+var(--lp-bottom))] pt-3 sm:rounded-3xl sm:pb-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] tabular-nums text-muted-foreground">{when || " "}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("cards.op_done_cta")}
            className="lp-press lp-tile flex h-7 w-7 items-center justify-center rounded-full text-foreground"
            data-testid="operation-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-3 text-center">
          <span
            className={cn(
              "mx-auto mb-2.5 flex h-12 w-12 items-center justify-center overflow-hidden rounded-[14px]",
              (!branded || CategoryGlyph) && "lp-tile"
            )}
          >
            {ownIcon ? (
              <OwnIcon icon={ownIcon} tone={tone} incoming={incoming} />
            ) : branded ? (
              <MerchantIcon name={title} />
            ) : CategoryGlyph ? (
              <CategoryGlyph className="h-5 w-5 text-muted-foreground" />
            ) : (
              <MerchantIcon name={title} className="rounded-[14px]" />
            )}
          </span>
          <p className="text-[12px] text-muted-foreground">{title}</p>
          <p className="mt-1 text-[30px] font-bold leading-tight tracking-[-0.035em] tabular-nums text-foreground">
            {incoming ? "+" : "−"}
            {money(op.amountMinor, op.scale)}
          </p>
          <span
            className={cn(
              "mt-2.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold",
              tone === "ok" && "bg-emerald-500/15 text-emerald-500",
              tone === "wait" && "bg-amber-500/15 text-amber-500",
              tone === "bad" && "bg-destructive/15 text-destructive-fg"
            )}
          >
            {t(`cards.${statusKey}`)}
          </span>
        </div>

        {rows.length > 0 && (
          <div className="lp-glass mt-4 overflow-hidden">
            {rows.map(([label, value, copyable], index) => {
              const Row = copyable ? "button" : "div";
              return (
                <Row
                  key={label}
                  {...(copyable ? { type: "button", onClick: () => copy(copyable, label) } : {})}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left",
                    index > 0 && "border-t border-[var(--lp-hair)]",
                    copyable && "lp-press"
                  )}
                >
                  <span className="shrink-0 text-[11px] text-muted-foreground">{label}</span>
                  <span className="flex min-w-0 items-center gap-1.5 text-[12px] tabular-nums text-foreground">
                    <span className="truncate">{value}</span>
                    {copyable ? <Copy className="h-3 w-3 shrink-0 text-muted-foreground" /> : null}
                  </span>
                </Row>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="lp-press lp-tile mt-3 w-full rounded-[13px] px-4 py-3 text-sm font-semibold text-foreground"
        >
          {t("cards.op_done_cta")}
        </button>
      </div>
    </div>
  );
};

export default OperationSheet;
